// Tipos específicos para animaciones de framer-motion
import type { Variant } from "framer-motion";

// Tipo para spring animations que acepta string en type
export interface SpringTransition {
  type: "spring" | "tween" | "keyframes" | "inertia";
  stiffness?: number;
  damping?: number;
  mass?: number;
  duration?: number;
  ease?: string | number[];
}

// Extensión del tipo Variant para incluir nuestras transiciones
export interface CustomVariant extends Omit<Variant, "transition"> {
  transition?: SpringTransition;
  x?: number | string;
  y?: number | string;
  opacity?: number;
  scale?: number;
  filter?: string;
}

// Tipo personalizado para nuestros variants
export interface CustomVariants {
  [key: string]: CustomVariant;
}

// Función helper para convertir tipos string a AnimationGeneratorType
export function createSpringTransition(
  stiffness: number = 150,
  damping: number = 20,
  duration?: number,
  delay?: number
) {
  return {
    type: "spring" as const,
    stiffness,
    damping,
    ...(duration && { duration }),
    ...(delay && { delay }),
  };
}

// Función helper para ease types
export function createTweenTransition(
  duration: number = 0.3,
  ease: string = "easeInOut",
  delay?: number
) {
  return {
    type: "tween" as const,
    duration,
    ease: ease as any, // Forzar el tipo para compatibilidad
    ...(delay && { delay }),
  };
}

// Variants predefinidos y tipados correctamente
export const commonVariants = {
  // Animaciones básicas
  slideUp: {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  slideDown: {
    hidden: { y: -10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  slideLeft: {
    hidden: { x: 20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  slideRight: {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  fadeIn: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: createTweenTransition(0.3),
    },
  } as const,

  fadeOut: {
    initial: { opacity: 1 },
    exit: {
      opacity: 0,
      transition: createTweenTransition(0.3),
    },
  } as const,

  scaleIn: {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  // Variants para navegación
  navItem: {
    hidden: { y: -10, opacity: 0, filter: "blur(4px)" },
    visible: {
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: createSpringTransition(150, 20),
    },
  } as const,

  navbar: {
    hidden: { y: -10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: createSpringTransition(150, 20, 0.5),
    },
  } as const,

  // Variants para móviles
  mobilePanel: {
    hidden: { x: "100%", opacity: 0, scale: 0.95 },
    visible: {
      x: "0%",
      opacity: 1,
      scale: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  mobileMenuItem: {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  // Variants para sidebar
  sidebarSlide: {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  sidebarPanel: {
    hidden: { x: "-100%", opacity: 0 },
    visible: {
      x: "0%",
      opacity: 1,
      transition: createTweenTransition(0.3),
    },
  } as const,

  // Variants para tarjetas
  cardHover: {
    scale: 1.05,
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    transition: createSpringTransition(150, 20),
  } as const,

  cardScale: {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: createSpringTransition(150, 20),
    },
  } as const,

  // Variants para servicios
  serviceCard: {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: createTweenTransition(0.6, "easeOut", custom * 0.1),
    }),
    hover: {
      scale: 1.05,
      transition: createSpringTransition(200, 25),
    },
  } as const,

  serviceIcon: {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: createSpringTransition(200, 25, 0.3),
    },
    hover: {
      rotate: 360,
      scale: 1.1,
      transition: createSpringTransition(200, 25),
    },
  } as const,

  // Variants para títulos
  titleSlide: {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: createTweenTransition(0.6, "easeOut"),
    },
  } as const,

  titleSlideX: {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: createTweenTransition(0.6, "easeOut"),
    },
  } as const,

  // Variants para CTA
  ctaSlide: {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: createTweenTransition(0.6, "easeOut", 0.4),
    },
  } as const,

  // Variants para iconos
  iconContainer: {
    hover: {
      scale: 1.1,
      transition: createSpringTransition(200, 25),
    },
  } as const,

  // Variants para loader
  loaderContainer: {
    initial: { opacity: 0 },
    exit: {
      opacity: 0,
      transition: createTweenTransition(0.3),
    },
  } as const,

  letterFade: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: createTweenTransition(0.3),
    },
  } as const,
};

// Función para crear variants personalizados cuando se necesite
export function createCustomVariants(options: {
  hiddenY?: number;
  hiddenX?: number;
  hiddenOpacity?: number;
  hiddenScale?: number;
  visibleDelay?: number;
  springStiffness?: number;
  springDamping?: number;
}): any {
  const {
    hiddenY = 0,
    hiddenX = 0,
    hiddenOpacity = 0,
    hiddenScale = 1,
    visibleDelay = 0,
    springStiffness = 150,
    springDamping = 20,
  } = options;

  return {
    hidden: {
      y: hiddenY,
      x: hiddenX,
      opacity: hiddenOpacity,
      scale: hiddenScale,
    },
    visible: {
      y: 0,
      x: 0,
      opacity: 1,
      scale: 1,
      transition: createSpringTransition(
        springStiffness,
        springDamping,
        undefined,
        visibleDelay
      ),
    },
  } as const;
}
